import React, { useMemo, useState } from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import GuildSidebar from './GuildSidebar';
import VaultHeader from './VaultHeader';
import RaritySection from './RaritySection';
import { SearchIcon } from '../shared/RelicVaultIcons';
import { useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import { COLORS, bodyFont, headingFont } from '../shared/theme';
import {
  OWNED_FILTERS,
  buildRaritySections,
  relicsForGuild,
} from 'utils/relicVault/catalog';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

/**
 * Every guild's full relic collection, owned vs. undiscovered, searchable and
 * filterable, with a sell-back flow. Sits directly under the relic store on the
 * Relics tab and shares its ownership, so a relic bought above shows as
 * discovered here straight away.
 */
const VaultBrowser = ({ catalog, vault, browser }) => {
  const { isTablet, isPhone } = useRelicVaultLayout();

  const currentRelics = useMemo(
    () => relicsForGuild(catalog, browser.selectedGuildId, vault.ownedRelicIds),
    [catalog, browser.selectedGuildId, vault.ownedRelicIds],
  );
  const sections = useMemo(
    () =>
      buildRaritySections(
        catalog,
        currentRelics,
        browser.selectedGuildId,
        browser.searchQuery,
        browser.ownedFilter,
      ),
    [catalog, currentRelics, browser.selectedGuildId, browser.searchQuery, browser.ownedFilter],
  );

  const columns = isPhone ? 2 : isTablet ? 3 : 5;
  const gap = isPhone ? 8 : isTablet ? 10 : 13;

  return (
    <View style={styles.root}>
    <View style={[styles.layout, isTablet && styles.layoutStacked]}>
        <View style={isTablet ? styles.sidebarStacked : styles.sidebarColumn}>
          <GuildSidebar
            catalog={catalog}
            ownedRelicIds={vault.ownedRelicIds}
            ownedCompanionIds={vault.ownedCompanionIds}
            selectedGuildId={browser.selectedGuildId}
            horizontal={isTablet}
            onSelect={browser.selectGuild}
          />
        </View>

        <View style={styles.main}>
          <VaultHeader
            catalog={catalog}
            selectedGuildId={browser.selectedGuildId}
            currentRelics={currentRelics}
            isTablet={isTablet}
            isPhone={isPhone}
          />

          <View style={[styles.toolbar, isPhone && styles.toolbarPhone]}>
            <View style={[styles.search, isPhone && styles.searchPhone]}>
              <SearchIcon />
              <TextInput
                value={browser.searchQuery}
                onChangeText={browser.setSearchQuery}
                placeholder="Search relics..."
                placeholderTextColor={COLORS.dim}
                autoCorrect={false}
                style={[styles.searchInput, webOnly({ outlineStyle: 'none' })]}
              />
            </View>
            <View style={[styles.filters, isPhone && styles.filtersPhone]}>
              {OWNED_FILTERS.map(filter => (
                <FilterPill
                  key={filter.value}
                  label={filter.label}
                  active={browser.ownedFilter === filter.value}
                  onPress={() => browser.setOwnedFilter(filter.value)}
                />
              ))}
            </View>
          </View>

          {sections.map(section => (
            <RaritySection
              key={section.key}
              catalog={catalog}
              section={section}
              selectedRelicId={browser.selectedRelicId}
              columns={columns}
              gap={gap}
              isPhone={isPhone}
              onSelect={browser.selectRelic}
            />
        ))}
      </View>
    </View>
    </View>
  );
};

const FilterPill = ({ label, active, onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      style={[
        styles.filterPill,
        hovered && !active && styles.filterPillHover,
        active && styles.filterPillActive,
      ]}>
      <Text style={[styles.filterPillText, active && { color: '#FFFFFF' }]}>{label}</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  topnav: {
    width: '100%',
    maxWidth: 1392,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 18,
    paddingHorizontal: 24,
    backgroundColor: 'rgba(18,15,42,0.97)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.09)',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    zIndex: 60,
  },
  topnavTablet: {
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  topnavLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  topnavLogo: {
    width: 36,
    height: 36,
  },
  topnavWord: {
    ...headingFont('800'),
    fontSize: 19,
    color: COLORS.textWarm,
    marginLeft: 10,
  },
  navLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 32,
  },
  navLinksTablet: {
    marginLeft: 16,
  },
  navLink: {
    paddingBottom: 5,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginRight: 26,
  },
  navLinkActive: {
    borderBottomColor: COLORS.accent,
  },
  navLinkText: {
    ...bodyFont('700'),
    fontSize: 14,
    color: COLORS.muted,
  },
  topnavRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  coinPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 16,
  },
  coinPillGlow: {
    borderRadius: 999,
  },
  coinPillGlowNative: {
    borderWidth: 3,
    borderColor: 'rgba(255,216,115,0.6)',
  },
  coinBalance: {
    ...headingFont('700'),
    fontSize: 15,
    color: COLORS.gold,
    marginLeft: 8,
  },
  closeBtn: {
    marginLeft: 12,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    width: '100%',
    maxWidth: 1392,
    alignSelf: 'center',
    paddingTop: 26,
    paddingHorizontal: 24,
    paddingBottom: 80,
  },
  contentTablet: {
    paddingTop: 20,
    paddingHorizontal: 16,
    paddingBottom: 64,
  },
  contentPhone: {
    paddingTop: 16,
    paddingHorizontal: 12,
    paddingBottom: 56,
  },
  layout: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  layoutStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  sidebarColumn: {
    width: 224,
    marginRight: 20,
  },
  sidebarStacked: {
    marginBottom: 20,
  },
  main: {
    flex: 1,
    minWidth: 0,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 20,
  },
  toolbarPhone: {
    flexDirection: 'column',
    alignItems: 'stretch',
    // Column + wrap makes Yoga lay children out by height and squashes them
    // (the filter pills collapsed to a few px tall). Stacking never wraps.
    flexWrap: 'nowrap',
  },
  search: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 220,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginRight: 12,
  },
  searchPhone: {
    marginRight: 0,
    minWidth: 0,
    // Stacked, flex grow/shrink would size the search box by height instead of width.
    flexGrow: 0,
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  searchInput: {
    ...bodyFont('400'),
    flex: 1,
    fontSize: 13,
    color: COLORS.text,
    marginLeft: 9,
    padding: 0,
  },
  filters: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filtersPhone: {
    marginTop: 12,
  },
  filterPill: {
    paddingVertical: 8,
    paddingHorizontal: 13,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.14)',
    marginRight: 7,
  },
  filterPillHover: {
    borderColor: 'rgba(255,255,255,0.3)',
  },
  filterPillActive: {
    backgroundColor: COLORS.accent,
    borderColor: COLORS.accent,
  },
  filterPillText: {
    ...headingFont('700'),
    fontSize: 11,
    color: COLORS.lilacSoft,
  },
});

export default VaultBrowser;
